import React from 'react';
import { useTranslation } from 'react-i18next';
// core
import {
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
} from '@material-ui/core';
// icons
import DeleteIcon from '@material-ui/icons/Close';
import Filter2Icon from '@material-ui/icons/Filter2';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import useStyles from '../styles';
import ExampleImageQuestion from '../../ExampleImageQuestion';
import EmailsBackup from '../../EmailsBackup';
import CategorySelect from './CategorySelect';

import { VIEW_MODE, EDIT_MODE } from '../../../utils/const/taskModes';
import { MODULES, FORMATS } from '../../../utils/const/quillModules';

/**
 * Chrome común a todos los tipos de pregunta: numerador, título, pregunta corta,
 * descripción, botones de media/emails, preview de la imagen de referencia,
 * duplicar/eliminar y los modales de `ExampleImageQuestion` y `EmailsBackup`.
 *
 * El cuerpo propio de cada tipo (opciones, selects, switches, modales de KPI)
 * va en `children`, que se renderiza entre la descripción y los botones.
 *
 * Se espera recibir el estado de `useQuestionForm` (normalmente con `{...q}`);
 * cualquier prop declarada después del spread pisa el valor del hook, que es la
 * forma de inyectar un `handleOnChange` propio.
 */
function QuestionShell({
  identifier,
  mode,
  info,
  // textos (claves i18n)
  titleKey,
  descriptionKey,
  titleMaxLength = 250,
  // 'question' (default) | 'welcomeTitle'
  titleVariant = 'question',
  // comportamiento
  disabled = mode === VIEW_MODE,
  editable = mode === EDIT_MODE,
  // 'quill' (default) | 'text' | 'none'
  descriptionAs = 'quill',
  // sólo para 'text': valor externo cuando el tipo no usa el estado del hook
  descriptionValue,
  descriptionMaxLength = 140,
  showMedia = true,
  showImagePreview = true,
  actionsInGrid = false,
  actionsInBox = false,
  extraActions = null,
  // estado del formulario (useQuestionForm)
  localTitle,
  localShortQuestion,
  categoryId,
  handleCategoryChange,
  description,
  setDescription,
  handleOnChange,
  handleBlur,
  image,
  setImage,
  file,
  setFile,
  open,
  setOpen,
  openEmails,
  setOpenEmails,
  sendEmail,
  setSendEmail,
  emailsArray,
  setEmailsArray,
  // acciones de la fila
  handleDeleteQuestion,
  handleDuplicateQuestion,
  children,
  footer = null,
}) {
  const styles = useStyles();
  const { t } = useTranslation();

  const fieldClassName = styles[titleVariant];
  const onFieldChange = editable ? handleOnChange : null;

  const mediaButtons = showMedia && mode !== VIEW_MODE ? (
    <>
      <Button onClick={() => setOpen(true)}>
        {t('task.taskForm.addMediaTitle')}
      </Button>
      <Button onClick={() => setOpenEmails(true)}>
        {t('task.taskForm.addEmails')}
      </Button>
    </>
  ) : null;

  const renderDescription = () => {
    if (descriptionAs === 'none') return null;

    if (descriptionAs === 'text') {
      return (
        <TextField
          id="description"
          className={styles.description}
          placeholder={t(descriptionKey)}
          value={descriptionValue !== undefined ? descriptionValue : description}
          onChange={onFieldChange}
          onBlur={handleBlur}
          InputProps={{ disableUnderline: true }}
          inputProps={{ maxLength: descriptionMaxLength }}
          multiline
          disabled={disabled}
        />
      );
    }

    return (
      <ReactQuill
        className={styles.reactQuill}
        modules={MODULES}
        formats={FORMATS}
        value={description}
        readOnly={mode === VIEW_MODE}
        placeholder={t(descriptionKey)}
        onChange={
          editable
            ? (value) => {
              setDescription(value);
              handleOnChange({
                target: {
                  id: 'description',
                  name: 'description',
                  value,
                },
              });
            }
            : null
        }
      />
    );
  };

  const renderRowActions = () => {
    if (mode === VIEW_MODE) return null;

    const buttons = (
      <>
        <Tooltip title={t('task.taskEdition.duplicate')}>
          <IconButton
            id={identifier}
            onClick={handleDuplicateQuestion}
            className={styles.deleteButton}
          >
            <Filter2Icon />
          </IconButton>
        </Tooltip>
        <IconButton
          id={identifier}
          onClick={handleDeleteQuestion}
          className={styles.deleteButton}
        >
          <DeleteIcon />
        </IconButton>
      </>
    );

    return actionsInBox ? <Box>{buttons}</Box> : buttons;
  };

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <p className={styles.identifier}>{identifier}.</p>
        <div className={styles.questionContainer}>
          <TextField
            id="title"
            className={fieldClassName}
            placeholder={t(titleKey)}
            value={localTitle}
            onChange={onFieldChange}
            onBlur={handleBlur}
            InputProps={{ disableUnderline: true }}
            inputProps={{ maxLength: titleMaxLength }}
            multiline
            disabled={disabled}
          />
          <TextField
            id="shortQuestion"
            className={fieldClassName}
            placeholder={mode !== VIEW_MODE ? t('task.taskEdition.shortQuestionPlaceholder') : ''}
            value={localShortQuestion}
            onChange={onFieldChange}
            // Al perder el foco se aplica lo que quedó pendiente, así hacer clic
            // en Guardar justo después de escribir no descarta la última edición.
            onBlur={handleBlur}
            InputProps={{ disableUnderline: true }}
            inputProps={{ maxLength: 250 }}
            multiline
            disabled={disabled}
          />
          {renderDescription()}
          {children}
          {editable ? (
            <CategorySelect
              categoryId={categoryId}
              onChange={handleCategoryChange}
              disabled={disabled}
            />
          ) : null}
          {actionsInGrid ? (
            <Grid container direction="column">
              <Grid item xs>{mediaButtons}</Grid>
              {extraActions ? <Grid item xs>{extraActions}</Grid> : null}
            </Grid>
          ) : (
            <>
              {mediaButtons}
              {extraActions}
            </>
          )}
          {showImagePreview && image?.url ? (
            <Grid container>
              <Grid item xs={1}>
                <Tooltip
                  title={
                    <img
                      alt="reference-image"
                      src={image.url}
                      width="100%"
                      height="100%"
                    />
                  }
                  arrow
                  placement="top-end"
                >
                  <img
                    alt="reference-image"
                    src={image.url}
                    width="50px"
                    height="50px"
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={5}>
                <List className={styles.list}>
                  <ListItem>
                    <ListItemText
                      primary={image.name}
                      primaryTypographyProps={{ variant: 'h5' }}
                      secondary={image.size ? `  (${image.size})` : null}
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          ) : null}
        </div>
      </div>
      {renderRowActions()}
      {showMedia ? (
        <>
          <ExampleImageQuestion
            open={open}
            setOpen={setOpen}
            file={file}
            setFile={setFile}
            handleOnChange={handleOnChange}
            setImage={setImage}
            image={image}
          />
          <EmailsBackup
            sendEmail={sendEmail}
            setSendEmails={setSendEmail}
            open={openEmails}
            setOpen={setOpenEmails}
            emailsArray={emailsArray}
            setEmailsArray={setEmailsArray}
            handleOnChange={handleOnChange}
            disabled={!!info}
          />
        </>
      ) : null}
      {footer}
    </div>
  );
}

export default QuestionShell;
